import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import HeadquartersPage from "../components/headquarters/HeadquartersPage";
import HeadquartersCard from "../components/headquarters/HeadquartersCard";

/*
========================================================
PLATFORM TENANTS PAGE — COCKPIT STYLE
--------------------------------------------------------
Purpose:
- Lists all tenants in the system
- Entry point into each tenant cockpit
- Uses JobTelesis AppShell visual language
========================================================
*/

function fmtDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function isActiveTenant(t) {
  return !t.is_suspended && String(t.subscription_status || "active") !== "inactive";
}

export default function PlatformTenants() {
  const [tenants, setTenants] = useState([]);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");

  async function loadTenants() {
    setErr("");

    try {
      const res = await fetch("/api/platform/tenants", {
        credentials: "include",
        headers: { "x-tenant": "dfasio" },
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Failed to load tenants");
      }

      const data = await res.json();
      setTenants(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e.message || "Error loading tenants");
    }
  }

  useEffect(() => {
    loadTenants();
  }, []);

  const filteredTenants = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenants;

    return tenants.filter((t) =>
      [t.name, t.subdomain, String(t.id)]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [tenants, search]);

  const totalTenants = tenants.length;
  const activeTenants = tenants.filter(isActiveTenant).length;
  const suspendedTenants = tenants.filter((t) => t.is_suspended).length;

  const newThisMonth = tenants.filter((t) => {
    if (!t.created_at) return false;
    const created = new Date(t.created_at);
    const now = new Date();
    return (
      created.getFullYear() === now.getFullYear() &&
      created.getMonth() === now.getMonth()
    );
  }).length;

  const pageTitle = {
    margin: 0,
    fontSize: 34,
    lineHeight: 1.1,
    fontWeight: 900,
    color: "#ffffff",
  };

  const muted = {
    color: "#9fb0c3",
    marginTop: 8,
    marginBottom: 0,
  };

  const card = {
    background: "#13293d",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12,
    padding: 20,
    boxShadow: "0 10px 30px rgba(0,0,0,0.14)",
  };

  const metricGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: 16,
  };

  const metricCard = {
    background: "#0b1f35",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 12,
    padding: 18,
    minHeight: 92,
  };

  const label = {
    color: "#9fb0c3",
    fontSize: 13,
    marginBottom: 8,
  };

  const metricValue = {
    fontSize: 28,
    fontWeight: 900,
    color: "#ffffff",
  };

  const toolbar = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 18,
  };

  const input = {
    width: "min(420px, 100%)",
    padding: "12px 14px",
    borderRadius: 8,
    border: "1px solid #27496b",
    background: "#0b1f35",
    color: "#ffffff",
    outline: "none",
  };

  const tableWrap = {
    overflowX: "auto",
  };

  const table = {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 760,
  };

  const th = {
    textAlign: "left",
    color: "#d7e7f5",
    fontSize: 13,
    padding: "12px 10px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  };

  const td = {
    padding: "13px 10px",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    color: "#ffffff",
    verticalAlign: "middle",
  };

  const pill = {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "4px 9px",
    fontSize: 12,
    fontWeight: 800,
    background: "rgba(24, 163, 74, 0.18)",
    color: "#7ee39b",
    border: "1px solid rgba(126, 227, 155, 0.22)",
  };

  const button = {
    display: "inline-block",
    padding: "8px 12px",
    background: "#00c2ff",
    color: "#062033",
    borderRadius: 7,
    textDecoration: "none",
    fontWeight: 800,
    whiteSpace: "nowrap",
  };

  return (
    <HeadquartersPage
      title="Platform Tenants"
      subtitle="Manage all tenant workspaces across the JobTelesis platform."
      actions={
        <Link
          to="/signup"
          style={{
            ...button,
            background: "#f5c542",
            marginTop: 6,
          }}
        >
          + Add Tenant
        </Link>
      }
    >

      {err && (
        <div
          style={{
            color: "#ffb4b4",
            background: "rgba(255,80,80,0.12)",
            border: "1px solid rgba(255,80,80,0.25)",
            borderRadius: 12,
            padding: 14,
            marginBottom: 16,
          }}
        >
          {err}
        </div>
      )}

      <HeadquartersCard>
        <div style={metricGrid}>
          <div style={metricCard}>
            <div style={label}>Total Tenants</div>
            <div style={metricValue}>{totalTenants}</div>
            <div style={label}>All workspaces</div>
          </div>

          <div style={metricCard}>
            <div style={label}>Active Tenants</div>
            <div style={metricValue}>{activeTenants}</div>
            <div style={label}>Currently active</div>
          </div>

          <div style={metricCard}>
            <div style={label}>Suspended Tenants</div>
            <div style={metricValue}>{suspendedTenants}</div>
            <div style={label}>On hold</div>
          </div>

          <div style={metricCard}>
            <div style={label}>New This Month</div>
            <div style={metricValue}>{newThisMonth}</div>
            <div style={label}>Current month</div>
          </div>
        </div>
      </HeadquartersCard>

      <HeadquartersCard>
        <div style={toolbar}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tenants by name, subdomain, or ID..."
            style={input}
          />

          <div style={{ color: "#9fb0c3", fontSize: 13 }}>
            Showing {filteredTenants.length} of {totalTenants} tenants
          </div>
        </div>

        {filteredTenants.length === 0 ? (
          <div style={{ color: "#9fb0c3" }}>No tenants found.</div>
        ) : (
          <div style={tableWrap}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>ID</th>
                  <th style={th}>Tenant Name</th>
                  <th style={th}>Subdomain</th>
                  <th style={th}>Tenant Type</th>
                  <th style={th}>Created</th>
                  <th style={th}>Status</th>
                  <th style={th}>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredTenants.map((t) => (
                  <tr key={t.id}>
                    <td style={td}>{t.id}</td>
                    <td style={{ ...td, fontWeight: 800 }}>{t.name}</td>
                    <td style={td}>{t.subdomain}</td>
                    <td style={td}>
                      <span
                        style={{
                          ...pill,
                          background:
                            t.tenant_type === "beta"
                              ? "rgba(59,130,246,0.18)"
                              : "rgba(34,197,94,0.14)",
                          color: t.tenant_type === "beta" ? "#bfdbfe" : pill.color,
                        }}
                      >
                        {String(t.tenant_type || "standard")
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase())}
                      </span>
                    </td>
                    <td style={td}>{fmtDate(t.created_at)}</td>
                    <td style={td}>
                      <span
                        style={{
                          ...pill,
                          background: t.is_suspended
                            ? "rgba(255,80,80,0.14)"
                            : pill.background,
                          color: t.is_suspended ? "#ffb4b4" : pill.color,
                        }}
                      >
                        {t.is_suspended ? "Suspended" : "Active"}
                      </span>
                    </td>
                    <td style={td}>
                      <Link to={`/platform/tenants/${t.id}`} style={button}>
                        Open Cockpit →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </HeadquartersCard>
    </HeadquartersPage>
  );
}
